import type { PaginatedResponse } from "./courseCertificates";
import { cleApi } from "./client";
import type { AuthorshipEvidenceModel } from "./models/authorships";

export type {
  AuthorshipEvidenceListModel,
  AuthorshipEvidenceModel,
} from "./models/authorships";

/** @deprecated Prefer AuthorshipEvidenceModel for detail views */
export interface AuthorshipModel {
  id: string;
  type: "authorship";
  userId: string;
  bookTitle: string;
  publisher: string;
  publicationYear: number;
  isbn: string | null;
  numberOfLessons: number;
  createdAt: string;
  reported: boolean;
  reportId: string | null;
  status: string;
}

export interface GetAuthorshipsParams {
  userId: string;
  page?: number;
  pageSize?: number;
  reported?: boolean;
}

export interface GetAuthorshipByIdParams {
  userId: string;
  authorshipId: string;
}

export interface GetAuthorshipsByPeriodParams {
  userId: string;
  periodId: string;
}

export interface GetAuthorshipsByInjunctionParams {
  userId: string;
  injunctionId: string;
}

export interface GetAuthorshipsByImpositionParams {
  userId: string;
  impositionId: string;
}

export interface UpdateAuthorshipRequest {
  book_title?: string;
  publisher?: string;
  publication_year?: number;
  isbn?: string | null;
  number_of_lessons?: number;
}

export interface UpdateAuthorshipParams {
  userId: string;
  authorshipId: string;
  body: UpdateAuthorshipRequest;
}

export const getAuthorships = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: GetAuthorshipsParams): Promise<PaginatedResponse<AuthorshipModel>> => {
  const query = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });

  if (reported !== undefined) {
    query.set("reported", String(reported));
  }

  return cleApi.get<PaginatedResponse<AuthorshipModel>>(
    `users/${encodeURIComponent(userId)}/authorships?${query.toString()}`,
  );
};

export const getAuthorshipById = ({
  userId,
  authorshipId,
}: GetAuthorshipByIdParams): Promise<AuthorshipEvidenceModel> =>
  cleApi.get<AuthorshipEvidenceModel>(
    `users/${encodeURIComponent(userId)}/authorships/${encodeURIComponent(
      authorshipId,
    )}`,
  );

export const updateAuthorship = ({
  userId,
  authorshipId,
  body,
}: UpdateAuthorshipParams): Promise<AuthorshipEvidenceModel> =>
  cleApi.put<AuthorshipEvidenceModel>(
    `users/${encodeURIComponent(userId)}/authorships/${encodeURIComponent(authorshipId)}`,
    body,
  );

export const getAuthorshipsByPeriodId = ({
  userId,
  periodId,
}: GetAuthorshipsByPeriodParams): Promise<AuthorshipModel[]> =>
  cleApi.get<AuthorshipModel[]>(
    `users/${encodeURIComponent(userId)}/authorships/by-period/${encodeURIComponent(periodId)}`,
  );

export const getAuthorshipsByInjunctionId = ({
  userId,
  injunctionId,
}: GetAuthorshipsByInjunctionParams): Promise<AuthorshipModel[]> =>
  cleApi.get<AuthorshipModel[]>(
    `users/${encodeURIComponent(userId)}/authorships/by-injunction/${encodeURIComponent(injunctionId)}`,
  );

export const getAuthorshipsByImpositionId = ({
  userId,
  impositionId,
}: GetAuthorshipsByImpositionParams): Promise<AuthorshipModel[]> =>
  cleApi.get<AuthorshipModel[]>(
    `users/${encodeURIComponent(userId)}/authorships/by-imposition/${encodeURIComponent(impositionId)}`,
  );
